/**
 * VoiceNotesTimeline
 *
 * Lista cronológica de notas de voz de um contato, com:
 *  - Player de áudio (HTML5 audio)
 *  - Transcrição (status + texto)
 *  - Botão de retry se transcrição falhou
 *  - Botão de exclusão (autor ou admin)
 *  - Indicador de geolocalização se a nota foi gravada com GPS
 */

import {
  Loader2, Mic, AlertCircle, RefreshCw, Trash2,
  MapPin, Sparkles, CheckCircle2, Smile, Meh, Frown, MinusCircle,
  Lightbulb, Tags, TrendingUp, MessageSquare,
} from 'lucide-react';
import {
  useVoiceNotes, useDeleteVoiceNote, useRetryTranscription,
  type VoiceNote, type TranscriptionStatus, type Sentiment,
} from '../../hooks/useVoiceNotes';
import { toast } from 'sonner';

interface VoiceNotesTimelineProps {
  contactId: string;
}

const STATUS_LABEL: Record<TranscriptionStatus, { label: string; color: string }> = {
  pending:    { label: 'Aguardando…',  color: 'text-ink-muted' },
  processing: { label: 'Transcrevendo…', color: 'text-accent' },
  completed:  { label: 'Concluído',     color: 'text-emerald-300' },
  failed:     { label: 'Falhou',        color: 'text-danger' },
};

export function VoiceNotesTimeline({ contactId }: VoiceNotesTimelineProps) {
  const { data: notes, isLoading } = useVoiceNotes(contactId);
  const del   = useDeleteVoiceNote();
  const retry = useRetryTranscription();

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-accent" />
      </div>
    );
  }

  if (!notes || notes.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-hairline p-6 text-center">
        <Mic className="h-6 w-6 text-ink-subtle mx-auto mb-2" />
        <p className="text-sm text-ink-muted">Nenhuma nota de voz gravada ainda.</p>
        <p className="text-[11px] text-ink-faint mt-1">
          Use o gravador acima para registrar interações em campo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {notes.map((note) => (
        <VoiceNoteCard
          key={note.id}
          note={note}
          onRetry={() => retry.mutate(note, {
            onSuccess: () => toast.info('Transcrição reenviada'),
            onError:   (e) => toast.error('Falha no retry', { description: String(e) }),
          })}
          onDelete={() => {
            if (!confirm('Excluir esta nota de voz e seu áudio?')) return;
            del.mutate(note, {
              onSuccess: () => toast.success('Nota excluída'),
              onError:   (e) => toast.error('Falha ao excluir', { description: String(e) }),
            });
          }}
          isDeleting={del.isPending && del.variables?.id === note.id}
        />
      ))}
    </div>
  );
}

// ── Card individual ──────────────────────────────────────────────────────────

function VoiceNoteCard({
  note, onRetry, onDelete, isDeleting,
}: {
  note:       VoiceNote;
  onRetry:    () => void;
  onDelete:   () => void;
  isDeleting: boolean;
}) {
  const status = STATUS_LABEL[note.transcription_status];
  const dur    = formatDuration(note.duration_seconds);

  return (
    <div className="rounded-2xl card-premium p-4 space-y-3">
      {/* Cabeçalho */}
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center shrink-0">
          <Mic className="h-4 w-4 text-accent" />
        </div>

        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-ink-muted">
              {new Date(note.created_at).toLocaleString('pt-BR', {
                day:   '2-digit', month: '2-digit', year: '2-digit',
                hour:  '2-digit', minute: '2-digit',
              })}
            </span>
            <span className="text-[10px] text-ink-subtle">•</span>
            <span className="text-[10px] font-mono text-ink-subtle tabular-nums">{dur}</span>
            {note.latitude && note.longitude && (
              <>
                <span className="text-[10px] text-ink-subtle">•</span>
                <span className="flex items-center gap-0.5 text-[10px] text-ink-subtle" title={`${note.latitude.toFixed(4)}, ${note.longitude.toFixed(4)}`}>
                  <MapPin className="h-2.5 w-2.5" />
                  Localizada
                </span>
              </>
            )}
            <span className={`text-[10px] font-semibold ml-auto flex items-center gap-1 ${status.color}`}>
              {note.transcription_status === 'processing' && <Loader2 className="h-3 w-3 animate-spin" />}
              {note.transcription_status === 'completed'  && <CheckCircle2 className="h-3 w-3" />}
              {note.transcription_status === 'failed'     && <AlertCircle className="h-3 w-3" />}
              {status.label}
            </span>
          </div>

          {/* Player */}
          {note.audio_url && (
            <audio
              controls
              src={note.audio_url}
              className="w-full mt-2 h-8"
              preload="metadata"
            />
          )}
        </div>
      </div>

      {/* Transcrição */}
      {note.transcription_status === 'completed' && note.transcription && (
        <div className="rounded-xl bg-accent/5 border border-accent/15 p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="h-3 w-3 text-accent" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-accent">
              Transcrição (Whisper)
            </span>
          </div>
          <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">
            {note.transcription}
          </p>
        </div>
      )}

      {/* Insights NLU (Gemini) */}
      {note.nlu_status === 'processing' && note.transcription_status === 'completed' && (
        <div className="rounded-xl bg-midnight-3 border border-hairline p-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
          <span className="text-xs text-ink-muted">Analisando sentimento e temas…</span>
        </div>
      )}

      {note.nlu_status === 'completed' && (
        <NluInsightsBlock note={note} />
      )}

      {note.nlu_status === 'failed' && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 p-3 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 text-amber-300 shrink-0 mt-0.5" />
          <div className="flex-grow">
            <p className="text-xs font-semibold text-amber-200">Análise NLU falhou</p>
            <p className="text-[11px] text-amber-300/70 mt-0.5">
              {note.nlu_error?.slice(0, 200) ?? 'Erro desconhecido'}
            </p>
          </div>
        </div>
      )}

      {/* Status processing */}
      {note.transcription_status === 'processing' && (
        <div className="rounded-xl bg-accent/5 border border-accent/15 p-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
          <span className="text-xs text-ink-muted">
            Transcrevendo via OpenAI Whisper…
          </span>
        </div>
      )}

      {/* Status pending — Whisper ainda não foi disparado ou demora */}
      {note.transcription_status === 'pending' && (
        <div className="rounded-xl bg-midnight-3 border border-hairline p-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-subtle" />
          <span className="text-xs text-ink-muted">Aguardando início da transcrição…</span>
        </div>
      )}

      {/* Status failed */}
      {note.transcription_status === 'failed' && (
        <div className="rounded-xl bg-danger/10 border border-danger/25 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 text-danger shrink-0 mt-0.5" />
            <div className="flex-grow min-w-0">
              <p className="text-xs font-semibold text-danger">Falha na transcrição</p>
              {note.transcription_error && (
                <p className="text-[11px] text-danger/70 mt-0.5 break-words">
                  {note.transcription_error}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-semibold text-accent hover:underline flex items-center gap-1 cursor-pointer"
          >
            <RefreshCw className="h-3 w-3" />
            Tentar novamente
          </button>
        </div>
      )}

      {/* Ações */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="flex items-center gap-1 text-[11px] text-ink-subtle hover:text-danger transition-colors cursor-pointer disabled:opacity-50"
        >
          {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          Excluir
        </button>
      </div>
    </div>
  );
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── Bloco de insights NLU ────────────────────────────────────────────────────

const SENTIMENT_STYLE: Record<Sentiment, { label: string; color: string; bg: string; border: string; icon: typeof Smile }> = {
  positive: { label: 'Positivo', color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', icon: Smile },
  neutral:  { label: 'Neutro',   color: 'text-ink-muted',   bg: 'bg-midnight-3',     border: 'border-hairline',     icon: Meh    },
  negative: { label: 'Negativo', color: 'text-danger',      bg: 'bg-danger/10',      border: 'border-danger/25',    icon: Frown  },
  mixed:    { label: 'Misto',    color: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/25', icon: MinusCircle },
};

function NluInsightsBlock({ note }: { note: VoiceNote }) {
  const sentStyle = note.sentiment ? SENTIMENT_STYLE[note.sentiment] : null;
  const SentIcon  = sentStyle?.icon ?? Meh;

  const entities = note.mentioned_entities ?? {};
  const hasEntities =
    (entities.leaders?.length ?? 0) > 0 ||
    (entities.places?.length  ?? 0) > 0 ||
    (entities.demands?.length ?? 0) > 0 ||
    (entities.names?.length   ?? 0) > 0;

  return (
    <div className="rounded-xl bg-accent/5 border border-accent/20 p-3 space-y-3">
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-accent" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-accent">
          Análise (Gemini NLU)
        </span>
      </div>

      {/* Sumário */}
      {note.summary && (
        <p className="text-sm text-ink leading-relaxed">{note.summary}</p>
      )}

      {/* Sentimento + Engajamento */}
      <div className="flex flex-wrap items-center gap-2">
        {sentStyle && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${sentStyle.color} ${sentStyle.bg} ${sentStyle.border}`}>
            <SentIcon className="h-3 w-3" />
            {sentStyle.label}
          </span>
        )}
        {note.engagement_indicator !== null && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border text-accent bg-accent/10 border-accent/25">
            <TrendingUp className="h-3 w-3" />
            Engajamento: {note.engagement_indicator}
          </span>
        )}
      </div>

      {/* Temas */}
      {note.themes.length > 0 && (
        <div className="flex items-start gap-2">
          <Tags className="h-3 w-3 text-ink-subtle shrink-0 mt-1" />
          <div className="flex flex-wrap gap-1">
            {note.themes.map((t) => (
              <span key={t} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-midnight-3 border border-hairline text-ink-muted">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Entidades mencionadas */}
      {hasEntities && (
        <div className="flex items-start gap-2">
          <MessageSquare className="h-3 w-3 text-ink-subtle shrink-0 mt-1" />
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            {(entities.leaders?.length ?? 0) > 0 && (
              <span><span className="text-ink-subtle">Lideranças:</span> <span className="text-ink">{entities.leaders!.join(', ')}</span></span>
            )}
            {(entities.places?.length ?? 0) > 0 && (
              <span><span className="text-ink-subtle">Locais:</span> <span className="text-ink">{entities.places!.join(', ')}</span></span>
            )}
            {(entities.demands?.length ?? 0) > 0 && (
              <span><span className="text-ink-subtle">Demandas:</span> <span className="text-ink">{entities.demands!.join(', ')}</span></span>
            )}
            {(entities.names?.length ?? 0) > 0 && (
              <span><span className="text-ink-subtle">Pessoas:</span> <span className="text-ink">{entities.names!.join(', ')}</span></span>
            )}
          </div>
        </div>
      )}

      {/* Próxima ação sugerida */}
      {note.next_action_suggestion && (
        <div className="flex items-start gap-2 pt-2 hairline-t">
          <Lightbulb className="h-3 w-3 text-amber-300 shrink-0 mt-1" />
          <div className="flex-grow">
            <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300/80 mb-0.5">
              Próxima ação sugerida
            </p>
            <p className="text-xs text-ink">{note.next_action_suggestion}</p>
          </div>
        </div>
      )}
    </div>
  );
}
